import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'
import { FormProvider, useForm } from 'react-hook-form'

import {
    type PaymentMethod,
    useCreateSberPayPayment,
    useCreateTBankPayment,
    useCreateTBankSBPPayment,
    useCreateWalletOnePayment,
    useGetWalletInfo,
} from '@/shared/api'
import { useAppNavbar } from '@/shared/hooks/use-app-navbar'
import {
    type TDriverRechargeOnboardingKeys,
    useLocalStorage,
} from '@/shared/hooks/use-local-storage'
import { useModal } from '@/shared/hooks/use-modal'
import { currencyParser } from '@/shared/lib/currency-parser'
import { AppsflyerEvent, sendAppsflyer } from '@/shared/metrics'
import { BottomSheet, Button } from '@/shared/ui'

import { emptyPaymentMethod } from '@/features/payment-method'

import { CustomStories, DriverRechargeStories, DriverRechargeTooltip } from '@/widgets'

import {
    type TFormValues,
    useDriverRechargeMetricsStore,
    useMetrics,
    useOnRechargeSubmit,
    useResetPaymentLoadingOnReturn,
} from '../model'

import { BottomSheetAmount } from './bottom-sheet-amount'
import { BottomSheetRadio } from './bottom-sheet-radio'
import { DriverRechargeAmount } from './driver-recharge-amount'
import { DriverRechargeLoading } from './driver-recharge-loading'
import { DriverRechargeRadio } from './driver-recharge-radio'

import styles from './driver-recharge.module.scss'

export const DriverRecharge = (): React.ReactNode => {
    const navigation = useNavigate()
    const resetSelectedTagAmount = useDriverRechargeMetricsStore(
        (state) => state.resetSelectedTagAmount,
    )
    // Модалки
    const {
        isModalOpen: isModalOpenAmount,
        openModal: openModalAmount,
        closeModal: closeModalAmount,
        closeHandlerReady: closeHandlerReadyAmount,
        triggerClose: triggerCloseAmount,
    } = useModal()
    const {
        isModalOpen: isModalOpenRadio,
        openModal: openModalRadio,
        closeModal: closeModalRadio,
        closeHandlerReady: closeHandlerReadyRadio,
        triggerClose: triggerCloseRadio,
    } = useModal()

    // Для локальной/тестовой среды направляем запросы инициализации платежа в mock-сервис
    const proxyMockClient =
        __GLOBAL__.VITE_PROXY_MOCK_ENABLE === 'true' ?
            { client: { headers: { 'X-Proxy-Mock-Enable': 'true' } } }
        :   undefined

    // Запросы
    const {
        data: walletInfo,
        isLoading: isLoadingWalletInfo,
        isError: isWalletError,
    } = useGetWalletInfo()
    const {
        mutateAsync: mutateAsyncSberPay,
        isPending: isPendingSberPay,
        reset: resetSberPay,
    } = useCreateSberPayPayment(proxyMockClient)
    const {
        mutateAsync: mutateAsyncTBank,
        isPending: isPendingTBank,
        reset: resetTBank,
    } = useCreateTBankPayment(proxyMockClient)
    const {
        mutateAsync: mutateAsyncTBankSBP,
        isPending: isPendingTBankSBP,
        reset: resetTBankSBP,
    } = useCreateTBankSBPPayment(proxyMockClient)
    const {
        mutateAsync: mutateAsyncWalletOne,
        isPending: isPendingWalletOne,
        reset: resetWalletOne,
    } = useCreateWalletOnePayment(proxyMockClient)
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false)

    const resetPaymentLoading = useCallback(() => {
        setIsRedirecting(false)
        resetTBankSBP()
        resetTBank()
        resetWalletOne()
        resetSberPay()
    }, [resetTBankSBP, resetTBank, resetWalletOne, resetSberPay])

    useResetPaymentLoadingOnReturn(resetPaymentLoading)

    // localStorage
    const { getValue: getAmountStorage } = useLocalStorage<string>(
        'driver-recharge-amount',
        '30000',
    )
    const { getValue: getPaymentMethodStorage, setValue: setPaymentMethodStorage } =
        useLocalStorage<PaymentMethod>('driver-recharge-selected-method', emptyPaymentMethod)
    const maxBalance = 1_800_000
    // Onboarding
    const { getValue: onboardingStatusStorage, setValue: setOnboardingStatusStorage } =
        useLocalStorage<TDriverRechargeOnboardingKeys>('driver-recharge-onboarding', 'firstShow')
    const showOnboarding =
        onboardingStatusStorage() === 'firstShow' || onboardingStatusStorage() === 'secondShow'
    const [showStories, setShowStories] = useState<boolean>(showOnboarding)
    const closeStories = useCallback(() => {
        setShowStories(false)
    }, [])
    const [showTooltip, setShowTooltip] = useState<boolean>(showOnboarding)
    const formRef = useRef<HTMLFormElement>(null)
    const amountRef = useRef<HTMLDivElement>(null)
    const radioRef = useRef<HTMLDivElement>(null)

    // Настройки мобилки
    useAppNavbar(showStories ? ' ' : 'Пополнение счёта', null, null)

    useEffect(() => {
        resetSelectedTagAmount()
    }, [resetSelectedTagAmount])

    // Редирект на страницу ошибки при недоступности backend
    useEffect(() => {
        if (isWalletError) {
            navigation({
                to: '/driver-recharge/error',
                replace: true,
                state: { error: 'Ошибка загрузки данных' } as Record<string, unknown>,
            })
        }
    }, [isWalletError, navigation])

    // Форма
    const formMethods = useForm<TFormValues>({
        defaultValues: {
            value: getAmountStorage(),
            paymentMethod: getPaymentMethodStorage(),
        },
        mode: 'onChange',
    })

    // Отправка события о просмотре страницы
    const { financeTopupViewEvent } = useMetrics({ formMethods })

    useEffect(() => {
        if (!walletInfo || showStories) {
            return
        }
        sendAppsflyer(AppsflyerEvent.finance_topup_view, financeTopupViewEvent)
    }, [walletInfo, financeTopupViewEvent, showStories])

    // Инициализация метода оплаты по умолчанию
    useEffect(() => {
        if (!walletInfo || walletInfo.payment_methods.length === 0) {
            return
        }

        const current = getPaymentMethodStorage()
        const fromStorage = walletInfo.payment_methods.find(
            (method) => method.id === current?.id && method.is_available,
        )

        const preferred = walletInfo.payment_methods.find(
            (method) => method.id === 'tbank-sbp' && method.is_available,
        )

        const normalized =
            fromStorage ??
            preferred ??
            walletInfo.payment_methods.find(
                (method) => method.is_available && method.is_available,
            ) ??
            emptyPaymentMethod

        if (current?.id === normalized.id) {
            return
        }

        setPaymentMethodStorage(normalized)
        formMethods.setValue('paymentMethod', normalized, { shouldDirty: false })
    }, [walletInfo, setPaymentMethodStorage, formMethods, getPaymentMethodStorage])

    const onRechargeSubmitCallback = useOnRechargeSubmit({
        formMethods,
        walletInfoBalance: walletInfo?.balance.amount ?? 0,
        maxBalance,
        openModalAmount,
        setIsRedirecting,
        mutateAsyncTBank,
        mutateAsyncTBankSBP,
        mutateAsyncSberPay,
        mutateAsyncWalletOne,
    })

    return (
        <>
            {/* Онбоардинг */}
            {showStories && !isLoadingWalletInfo && (
                <CustomStories setShowStories={closeStories} stories={DriverRechargeStories} />
            )}
            {!isLoadingWalletInfo && showTooltip && !showStories && (
                <DriverRechargeTooltip
                    amountRef={amountRef}
                    contentRef={formRef}
                    radioRef={radioRef}
                    setShowTooltip={setShowTooltip}
                    onClose={() => {
                        if (onboardingStatusStorage() === 'firstShow') {
                            setOnboardingStatusStorage('secondShow')
                        } else {
                            setOnboardingStatusStorage('showed')
                        }
                    }}
                />
            )}

            {isLoadingWalletInfo ?
                <DriverRechargeLoading />
            :   <FormProvider {...formMethods}>
                    <form
                        ref={formRef}
                        className={styles['driver-recharge']}
                        data-test-id='driver-recharge-form'
                        onSubmit={formMethods.handleSubmit(onRechargeSubmitCallback)}
                    >
                        <div className={styles['driver-recharge__top']}>
                            <div className={styles['driver-recharge__texts']}>
                                <h2 className={styles['driver-recharge__title']}>Укажите сумму</h2>
                                <p className={styles['driver-recharge__balance']}>
                                    Баланс:{' '}
                                    <span
                                        data-test-id='driver-recharge-balance'
                                        className={clsx(
                                            styles['driver-recharge__balance-bank'],
                                            walletInfo?.balance &&
                                                walletInfo.balance.amount <= 0 &&
                                                styles['driver-recharge__balance-bank_error'],
                                        )}
                                    >
                                        {walletInfo?.balance.amount ?
                                            currencyParser(walletInfo.balance.amount)
                                        :   0}{' '}
                                        {walletInfo?.balance.currency === 'RUB' ? '₽' : '$'}
                                    </span>
                                </p>
                            </div>
                            <DriverRechargeAmount ref={amountRef} openModal={openModalAmount} />
                        </div>

                        <div className={styles['driver-recharge__bottom']}>
                            <h3 className={styles['driver-recharge__bottom-title']}>
                                <>Выберите способ пополнения</>
                            </h3>
                            <DriverRechargeRadio ref={radioRef} openModal={openModalRadio} />
                            <div className={styles['driver-recharge__button']}>
                                <Button
                                    data-test-id='driver-recharge-submit'
                                    size='large'
                                    type='submit'
                                    variant='primary'
                                    isLoading={
                                        isPendingWalletOne ||
                                        isPendingTBank ||
                                        isPendingTBankSBP ||
                                        isRedirecting ||
                                        isPendingSberPay
                                    }
                                >
                                    Пополнить
                                </Button>
                            </div>
                        </div>
                    </form>

                    {/* Bottom sheets */}
                    {isModalOpenAmount && (
                        <BottomSheet
                            closeHandlerReady={closeHandlerReadyAmount}
                            title='Сумма'
                            onClose={closeModalAmount}
                        >
                            <BottomSheetAmount triggerClose={triggerCloseAmount} />
                        </BottomSheet>
                    )}

                    {isModalOpenRadio && (
                        <BottomSheet
                            closeHandlerReady={closeHandlerReadyRadio}
                            title='Способ пополнения'
                            onClose={closeModalRadio}
                        >
                            <BottomSheetRadio triggerClose={triggerCloseRadio} />
                        </BottomSheet>
                    )}
                </FormProvider>
            }
        </>
    )
}
